import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import { generateAndStoreKeyPair, deleteLocalKeys } from '../lib/crypto';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => localStorage.getItem('sg_token'));

  const setToken = useCallback((t) => {
    if (t) {
      localStorage.setItem('sg_token', t);
    } else {
      localStorage.removeItem('sg_token');
    }
    setTokenState(t);
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const res = await authApi.getMe();
          setUser(res.data.user);
        } catch (err) {
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token, setToken]);

  const login = useCallback(async (username, password) => {
    const res = await authApi.login(username, password);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  }, [setToken]);

  const register = useCallback(async (username, password) => {
    // ZERO-KNOWLEDGE: Generate keys client-side, private key stays in browser
    const publicKeyB64 = await generateAndStoreKeyPair(username.toLowerCase());
    const res = await authApi.register(username, password, publicKeyB64);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  }, [setToken]);

  const logout = useCallback(() => {
    if (user) {
      deleteLocalKeys(user.username || user.id).catch(() => {});
    }
    setToken(null);
    setUser(null);
  }, [setToken, user]);

  const updateProfile = useCallback(async (data) => {
    const res = await authApi.updateProfile(data);
    setUser(res.data.user);
    return res.data;
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout, updateProfile,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
